import { FormControl, FormControlLabel, InputLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { ChangeEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import HelpPopover from '../../../HelpPopover';
import { updateParameter } from '../DeviceCardBuilder';
import { DeviceCardProps } from '../DeviceCardProps';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useControlInitialization } from '../../../../hooks/controls/useControlInitialization';
import { MOTOR_DIRECTION, type MotorDirection } from '../../../../utils/motor-direction';
import UnsupportedCodeStatus from './UnsupportedCodeStatus';
import { isUnsupportedEnumRawValue, parseKnownEnumRawValue } from './unsupportedEnumValue';

type MotorDirectionControlProps = DeviceCardProps;

const MOTOR_DIRECTION_FORWARD = 'MotorDirection.FORWARD';
const MOTOR_DIRECTION_REVERSE = 'MotorDirection.REVERSE';
const MOTOR_DIRECTION_VALUES = [MOTOR_DIRECTION_FORWARD, MOTOR_DIRECTION_REVERSE] as const;

const parseMotorDirection = (value: unknown): MotorDirection =>
  parseKnownEnumRawValue(value, MOTOR_DIRECTION_VALUES, MOTOR_DIRECTION_FORWARD) === MOTOR_DIRECTION_REVERSE
    ? MOTOR_DIRECTION.reverse
    : MOTOR_DIRECTION.forward;

const MotorDirectionControl = (props: MotorDirectionControlProps) => {
  const { t } = useTranslation();
  const {
    value: motorDirection,
    setValue: setMotorDirection,
    unsupportedRawValue,
  } = useControlInitialization<MotorDirection>(props, {
    parseValue: parseMotorDirection,
    isUnsupportedRawValue: (rawValue) => isUnsupportedEnumRawValue(rawValue, MOTOR_DIRECTION_VALUES),
  });

  const onChange = (_event: ChangeEvent<HTMLInputElement>, value: string) => {
    setMotorDirection(value as MotorDirection);
    updateParameter(props, value === MOTOR_DIRECTION.forward ? 'MotorDirection.FORWARD' : 'MotorDirection.REVERSE');
  };

  // Ensure value is always defined - default to FORWARD if undefined
  const isUnsupported = Boolean(unsupportedRawValue);
  const safeValue = isUnsupported ? '' : motorDirection ?? MOTOR_DIRECTION.forward;

  return (
    <Stack direction="column" spacing={2}>
      <InputLabel id="motor-direction-radio-label">
        <Stack direction="row" spacing={1}>
          <Typography>
            <Trans i18nKey="motorCard.motor-direction-label">Motor Direction</Trans>
          </Typography>
          <HelpPopover id="motor-direction-popover" text={t('motorCard.motor-direction-info-tooltip')} />
        </Stack>
      </InputLabel>
      <UnsupportedCodeStatus
        code={unsupportedRawValue}
        descriptionI18nKey="controlPanel.devices.unsupportedEnumParameter.description"
        descriptionDefaultValue="Unsupported setting value: {{code}}"
      />
      <FormControl sx={{ marginTop: '0px !important' }}>
        <RadioGroup aria-labelledby="demo-radio-buttons-group-label" name="radio-buttons-group" value={safeValue} onChange={onChange}>
          <FormControlLabel
            value={MOTOR_DIRECTION.forward}
            control={<Radio />}
            label={
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Trans i18nKey="motorCard.motor-direction.forward">Forward</Trans>
                {safeValue === MOTOR_DIRECTION.forward ? <ArrowUpwardIcon fontSize="medium" sx={{ mt: -0.25, flexShrink: 0 }} aria-hidden /> : null}
              </Stack>
            }
          />
          <FormControlLabel
            value={MOTOR_DIRECTION.reverse}
            control={<Radio />}
            label={
              <Stack direction="row" spacing={1} alignItems="flex-end">
                <Trans i18nKey="motorCard.motor-direction.reverse">Reverse</Trans>
                {safeValue === MOTOR_DIRECTION.reverse ? <ArrowDownwardIcon fontSize="medium" sx={{ mb: -0.25, flexShrink: 0 }} aria-hidden /> : null}
              </Stack>
            }
          />
        </RadioGroup>
      </FormControl>
    </Stack>
  );
};

export default MotorDirectionControl;
